/**
 * Zustandsfreie Helfer fuer die Chat-Komponenten — Port aus lib/util/chat.ts.
 * Einzige Aenderung: modeColorCls liefert Tailwind-Klassen statt CSS-Variablen,
 * und toolActivityLabel kennt die neuen Werkzeuge (suche_befunde, grep_akte …).
 */
import type { Chat } from '../api/unimedica';

export function fmtTime(iso: string): string {
  try { return new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
}

export function modeLabel(mode: string): string {
  return ({
    patient: 'Akte', patient_md: 'Akte — in Dokumenten suchen', praxis: 'Alle Akten',
    wissen: 'Nachschlagen', general: 'Allgemein', review: 'Dokument-Prüfung',
  } as Record<string, string>)[mode] ?? mode;
}

export function modeColorCls(mode: string): string {
  return ({
    patient:    'bg-primary-50 text-primary-500 border-primary-300',
    patient_md: 'bg-primary-50 text-primary-500 border-primary-300',
    praxis:     'bg-gray-50 text-ps-dark-700 border-gray-300',
    wissen:     'bg-emerald-50 text-emerald-800 border-emerald-200',
    general:    'bg-gray-50 text-ps-dark-500 border-gray-200',
    review:     'bg-amber-50 text-amber-800 border-amber-200',
  } as Record<string, string>)[mode] ?? 'bg-gray-50 text-ps-dark-500 border-gray-200';
}

export function toolActivityLabel(name: string): string {
  return ({
    read_patient_bundle: 'Lese die Patientenakte …',
    read_file: 'Öffne ein Dokument …',
    list_patient_files: 'Sichte die Dokumente der Akte …',
    suche_befunde: 'Durchsuche die Befunde …',
    suche_befunde_praxisweit: 'Durchsuche die Befunde aller Akten …',
    labor_praxisweit: 'Frage die Labordatenbank aller Akten ab …',
    grep_akte: 'Suche in den Dokumenten …',
    query_akte: 'Lese Diagnosen und Medikation …',
    query_labor: 'Lese Laborwerte …',
    list_patients: 'Suche alle Patienten …',
    grep_patients: 'Suche in allen Akten …',
    search_by_diagnosis: 'Suche nach passender Diagnose …',
    query_patients: 'Suche Patienten nach Kriterien …',
    web_search: 'Suche im Internet …',
    web_fetch: 'Lese eine Web-Seite …',
    compendium_search: 'Suche im Schweizer Arzneimittel-Compendium …',
    compendium_fetch: 'Lese Beipackzettel im Compendium …',
    pubmed_search: 'Suche in PubMed (wissenschaftliche Studien) …',
    europepmc_search: 'Suche in Europe PMC (Publikationen + Preprints) …',
    clinicaltrials_search: 'Suche klinische Studien (ClinicalTrials.gov) …',
  } as Record<string, string>)[name] ?? `Verarbeite (${name}) …`;
}

export function modePlaceholder(mode: string | undefined): string {
  if (mode === 'patient' || mode === 'patient_md') return 'Frag etwas zu dieser Akte — z.B. „Welche aktuellen Diagnosen?"';
  if (mode === 'praxis') return 'Such etwas in allen Akten — z.B. „Welche Patienten haben Diabetes?"';
  if (mode === 'wissen') return 'Stell eine medizinische Frage — z.B. „Dosierung von Metformin?"';
  return 'Schreib eine Nachricht …';
}

export function modeEmptyTitle(mode: string | undefined): string {
  if (mode === 'patient' || mode === 'patient_md') return 'Was möchtest du über diese Akte wissen?';
  if (mode === 'praxis') return 'Wonach möchtest du suchen?';
  if (mode === 'wissen') return 'Was möchtest du nachschlagen?';
  return 'Stell deine erste Frage.';
}

export function chatTitleDisplay(c: Chat): string { return c.title.replace(/^Patient:\s*/, 'Akte: '); }

export function patientLabelFromId(pid: string): string {
  const parts = pid.split('_');
  return parts.length >= 3 ? `${parts[1]} ${parts[2]}` : pid;
}

export function aktenNr(pid: string): string {
  const praxis = (pid ?? '').split('_')[0];
  return praxis && praxis !== 'XXXXX' ? `Akte ${praxis}` : '';
}

/** Parent-Chats nach updated_at, ihre Kinder (parent_chat_id) direkt darunter. */
export function sortChatsWithChildren(list: Chat[]): Chat[] {
  const byId = new Map(list.map((c) => [c.id, c]));
  const roots = list.filter((c) => !c.parent_chat_id || !byId.has(c.parent_chat_id));
  const childrenOf = new Map<string, Chat[]>();
  for (const c of list) {
    if (c.parent_chat_id && byId.has(c.parent_chat_id)) {
      const arr = childrenOf.get(c.parent_chat_id) ?? [];
      arr.push(c); childrenOf.set(c.parent_chat_id, arr);
    }
  }
  roots.sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at));
  const out: Chat[] = [];
  for (const r of roots) {
    out.push(r);
    out.push(...(childrenOf.get(r.id) ?? []).sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at)));
  }
  return out;
}

export function relTime(iso: string): string {
  try {
    const min = Math.floor((Date.now() - +new Date(iso)) / 60000);
    if (min < 1) return 'gerade eben';
    if (min < 60) return `vor ${min} Min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `vor ${h} Std`;
    const d = Math.floor(h / 24);
    if (d === 1) return 'gestern';
    if (d < 7) return `vor ${d} Tagen`;
    return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return ''; }
}
